import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { AccountSheet, Icons, NamedLink } from '../../components';
import css from './BottomNav.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { parse, stringify } from '../../util/urlHelpers';
import { fetchCategoryCounts } from '../../ducks/browse.duck';

/**
 * Phone bottom nav (browse redesign Phase 6, ledger "Phone & tablet
 * architecture"): Search · Browse · Home · Sell · Account with 11px labels.
 * Browse opens the category menu (the same surface as the topbar hamburger,
 * via the mobilemenu URL-modal convention); Inbox and Saved live under
 * Account. Sell is the primary CTA — the promo banner is dropped on phone.
 */
function BottomNav() {
  const notificationCount = useSelector(state => state.user.currentUserNotificationCount || 0);
  const dispatch = useDispatch();
  const [isAccountSheetOpen, setIsAccountSheetOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const openUrlModal = key => {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ [key]: 'open', ...parse(search) })}`;
    history.push(`${pathname}${searchString}`, state);
  };

  // Active tab (prototype): green + semibold for the surface in view
  const params = parse(location.search || '');
  const isBrowseOpen = params.mobilemenu === 'open';
  const isSearchOpen = params.mobilesearch === 'open';
  const itemClass = active => classNames(css.navItem, { [css.navItemActive]: active });
  const isHome = location.pathname === '/' && !isBrowseOpen && !isSearchOpen;

  return (
    // data-bottom-nav: the footer makes room for this bar on the pages that show it
    <div className={css.bottomNavbar} data-bottom-nav>
      <button className={itemClass(isSearchOpen)} onClick={() => openUrlModal('mobilesearch')}>
        <Icons name="bottomSearch" className={css.icon} />
        <span className={css.navLabel}>
          <FormattedMessage id="BottomNav.search" />
        </span>
      </button>
      <button
        className={itemClass(isBrowseOpen)}
        onClick={() => {
          if (isBrowseOpen) {
            const { mobilemenu, ...rest } = parse(location.search);
            history.push(`${location.pathname}?${stringify(rest)}`, location.state);
          } else {
            openUrlModal('mobilemenu');
            dispatch(fetchCategoryCounts());
          }
        }}
      >
        {/* Browse shares the topbar hamburger's two-line mark */}
        <Icons name="bottomBrowse" className={css.icon} />
        <span className={css.navLabel}>
          <FormattedMessage id="BottomNav.browse" />
        </span>
      </button>
      <NamedLink name="LandingPage" className={itemClass(isHome)}>
        <Icons name="navHome" className={css.icon} />
        <span className={css.navLabel}>
          <FormattedMessage id="BottomNav.home" />
        </span>
      </NamedLink>
      <NamedLink name="NewListingPage" className={css.navItem}>
        <Icons name="bottomSell" className={css.icon} />
        <span className={css.navLabel}>
          <FormattedMessage id="BottomNav.sell" />
        </span>
      </NamedLink>
      {/* Account opens the sheet (design canvas Option B) — the sheet
          handles both signed-in and guest content */}
      <button className={itemClass(isAccountSheetOpen)} onClick={() => setIsAccountSheetOpen(true)}>
        <span className={css.iconWithBadge}>
          <Icons name="bottomAccount" className={css.icon} />
          {notificationCount > 0 ? <span className={css.navBadge}>{notificationCount}</span> : null}
        </span>
        <span className={css.navLabel}>
          <FormattedMessage id="BottomNav.account" />
        </span>
      </button>
      <AccountSheet isOpen={isAccountSheetOpen} onClose={() => setIsAccountSheetOpen(false)} />
    </div>
  );
}

export default BottomNav;
